import { useRef, useState } from "react";
import { Play, Video as VideoIcon } from "lucide-react";
import { useContent, resolveImage } from "../contexts/ContentContext";

export const FounderVideo = () => {
  const { data: video } = useContent("founder_video");
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  if (!video || !video.video_url) return null;

  const start = () => {
    setPlaying(true);
    // wait for the <video> to be in the DOM, then play
    requestAnimationFrame(() => {
      ref.current?.play().catch(() => {});
    });
  };

  const poster = resolveImage(video.poster_url);
  const src = resolveImage(video.video_url);

  return (
    <section
      id="founder-video"
      data-testid="founder-video-section"
      className="py-20 md:py-28 bg-slate-50 relative overflow-hidden"
    >
      <div aria-hidden="true" className="absolute -top-20 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#2563EB]/10 blur-3xl" />
      <div aria-hidden="true" className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-[#FFD100]/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <span className="section-eyebrow">
              <VideoIcon className="w-3.5 h-3.5" />
              {video.eyebrow}
            </span>
            <h2
              data-testid="founder-video-heading"
              className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 leading-tight"
            >
              {video.heading?.split("Founder")[0]}
              <span className="text-gradient-brand">{video.heading?.includes("Founder") ? "Founder" : ""}</span>
              {video.heading?.includes("Founder") ? video.heading.split("Founder")[1] : ""}
            </h2>
            {video.intro && (
              <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-lg">
                {video.intro}
              </p>
            )}
            <div className="mt-6 flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 bg-white text-[#1D4ED8] text-xs font-bold px-3 py-1.5 rounded-full ring-1 ring-slate-200">
                <Play className="w-3 h-3" fill="currentColor" />
                Watch the message
              </span>
            </div>
          </div>

          {/* Player */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="relative rounded-[28px] overflow-hidden shadow-[0_30px_80px_rgba(15,23,42,0.18)] ring-1 ring-white bg-[#0F172A] aspect-video group">
              {playing ? (
                <video
                  ref={ref}
                  data-testid="founder-video-player"
                  src={src}
                  poster={poster || undefined}
                  controls
                  playsInline
                  preload="none"
                  className="absolute inset-0 w-full h-full object-cover bg-black"
                />
              ) : (
                <button
                  type="button"
                  onClick={start}
                  data-testid="founder-video-play"
                  aria-label="Play founder message"
                  className="absolute inset-0 w-full h-full block group"
                >
                  {poster ? (
                    <img
                      src={poster}
                      alt="Semaj Morgan — founder"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                      style={{ objectPosition: "center 25%" }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#1E3A8A] via-[#1D4ED8] to-[#2563EB]" />
                  )}
                  <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-[#0F172A]/20 to-transparent" />
                  {/* Play button */}
                  <span className="absolute inset-0 grid place-items-center">
                    <span className="relative inline-grid place-items-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#FFD100] text-[#0F172A] shadow-[0_20px_60px_rgba(255,209,0,0.55)] transition-transform duration-300 group-hover:scale-110">
                      <span aria-hidden="true" className="absolute inset-0 rounded-full bg-[#FFD100] opacity-50 animate-ping" />
                      <Play className="relative w-9 h-9 md:w-10 md:h-10 ml-1" fill="currentColor" strokeWidth={0} />
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
